interface IdleBudget { timeRemaining(): number }
export interface PreparationScheduler {
  idle(work: (budget: IdleBudget) => void): () => void;
  frame(): Promise<void>;
}

const scheduler: PreparationScheduler = {
  idle(work) {
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(work, { timeout: 500 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(() => work({ timeRemaining: () => 4 }), 32);
    return () => clearTimeout(id);
  },
  // Let the loading caption paint before the next synchronous figure build.
  frame: () => new Promise(resolve => {
    const done = () => { clearTimeout(timer); cancelAnimationFrame(frame); resolve(); };
    // Hidden tabs suspend animation frames; entry must still be able to complete there.
    const timer = setTimeout(done, 100);
    const frame = requestAnimationFrame(() => setTimeout(done, 0));
  }),
};

export const preparationFrame = scheduler.frame;

/** One synchronous figure per slice; a figure itself cannot be interrupted. Entry drains the same queue behind the overlay. */
export class IdlePreparation<T> {
  private items: T[];
  private cancelScheduled: (() => void) | null = null;
  private cancelled = false;
  private failed = false;
  private error: unknown;
  private finishing: Promise<void> | null = null;
  private built: (() => void) | null = null;
  constructor(items: readonly T[], private build: (item: T) => void, private scheduling = scheduler) { this.items = [...items]; }
  /** Items still waiting to be built. */
  get remaining() { return this.items.length; }
  start() {
    if (this.cancelled || this.failed || this.finishing || this.cancelScheduled || !this.items.length) return;
    this.cancelScheduled = this.scheduling.idle(budget => {
      this.cancelScheduled = null;
      if (this.cancelled) return;
      // Timeout callbacks can have no idle budget. Do not force work into a busy UI frame.
      if (budget.timeRemaining() >= 4) {
        try { this.build(this.items.shift()!); }
        catch (error) { this.failed = true; this.error = error; }
      }
      this.start();
    });
  }
  /** Builds what is left now; `built` hears each item this drain builds. */
  finish(built?: () => void): Promise<void> {
    if (built) this.built = built;
    if (this.finishing) return this.finishing;
    this.cancelScheduled?.(); this.cancelScheduled = null;
    return this.finishing = this.drain();
  }
  private async drain() {
    if (this.failed) throw this.error;
    while (!this.cancelled && this.items.length) {
      await this.scheduling.frame();
      if (this.cancelled) return;
      this.build(this.items.shift()!);
      this.built?.();
    }
  }
  cancel() {
    this.cancelled = true; this.items = [];
    this.cancelScheduled?.(); this.cancelScheduled = null;
  }
}
